import * as React from "react"
import styled from "styled-components"
import { IdentifiedLowerRate, ILRState, RateCheckInfo } from "../types"
import { SummaryDetails } from "./SummaryDetails"
import { mobile, tablet } from "components/_blueprint/Layout"
import { ToCurrency } from "lib/CurrencyFormatter"
import { formatAppointmentDate } from "lib/FormatDate"

type Props = {
  identifiedLowerRate?: IdentifiedLowerRate | null
  rateCheck: RateCheckInfo
}

export const RateCheckHeader = ({ identifiedLowerRate, rateCheck }: Props) => {
  const { LenderName, TrueCostOverInitialPeriod } = rateCheck?.customerProductDetails || {}

  const { loanAmount, propertyValue, termMonths } = rateCheck?.productInput || {}

  const formattedRateCheckDate = formatAppointmentDate(new Date(rateCheck?.lastChecked))

  const formattedTotalLoanAmount = ToCurrency(loanAmount)
  const formattedPropertyValue = ToCurrency(propertyValue)
  const termYears = Math.floor(termMonths / 12)
  const productLTV = Math.floor((loanAmount / propertyValue) * 100)

  const totalSavingsOverCurrRate =
    rateCheck?.customerProductDetails?.TrueCostOverInitialPeriod - TrueCostOverInitialPeriod

  const completionCutoff = rateCheck?.status === "stopped-completion-cutoff"
  return (
    <HeaderContainer>
      <TitleContainer>
        <Header>Rate Check Promise</Header>
        {rateCheck.lastChecked && (
          <SummaryCheckDateDesktop>
            Last rate check:<span>{formattedRateCheckDate}</span>
          </SummaryCheckDateDesktop>
        )}
      </TitleContainer>
      {completionCutoff ? (
        <>
          <TextContainer>
            <Subheader>Your final rate is confirmed</Subheader>
            <p>You‘re nearly at completion! Here‘s confirmation of your final rate.</p>
          </TextContainer>
        </>
      ) : (
        <>
          {identifiedLowerRate?.status === ILRState.Found && (
            <TextContainer>
              <Subheader>We’ve found you a lower rate</Subheader>
              <p>
                Great news! Your chosen lender {LenderName} has lowered their interest rates. You
                can review the new rate below and if you’d like to switch, simply request it below.
                We’ll begin the process as long as there’s time before your completion date.
              </p>
            </TextContainer>
          )}
          {identifiedLowerRate?.status === ILRState.Progressing && (
            <>
              <Subheader>We’re requesting your new interest rate</Subheader>
              <p>
                Thanks! We’ll contact {LenderName} to request the switch to your new rate. Please
                note, lenders have varying timescales and if you are too close to completion, it may
                not be approved. We’ll update you once it’s confirmed.
              </p>
            </>
          )}
          {identifiedLowerRate?.status === ILRState.Declined && (
            <TextContainer>
              <Subheader>You’re staying with your current rate</Subheader>
              <p>
                Thank you for confirming your choice. We’ll keep you on your current rate. We’ll
                continue to monitor the market and update you again if there is a lower rate with{" "}
                {LenderName} available.
              </p>
            </TextContainer>
          )}
          {(identifiedLowerRate?.status === ILRState.Cancelled ||
            identifiedLowerRate?.status === ILRState.Expired) && (
            <>
              <Subheader>We’re monitoring for lower rates</Subheader>
              <p>
                We’re still keeping any eye on the {LenderName}’s rates, and will update you if they
                lower. Right now, you’re still on the lowest rate for your chosen deal with{" "}
                {LenderName}.
              </p>
            </>
          )}
          {identifiedLowerRate?.status === ILRState.Completed && (
            <>
              <Subheader>You’ve moved onto a lower rate</Subheader>
              <p>
                Great news! {LenderName} have approved the move onto the lower rate, meaning you’ve
                just saved {ToCurrency(totalSavingsOverCurrRate)} in interest over the initial
                period of your mortgage.
              </p>
            </>
          )}
          {!identifiedLowerRate && (
            <>
              <Subheader>Monitoring for lower rates</Subheader>
              <p>
                Our Rate Check Promise technology reviews the market daily for lower rates with the
                lender you have applied with. If we find a lower rate, and can switch you before
                your completion date, we’ll notify you immediately.
              </p>
            </>
          )}
        </>
      )}
      <SummaryDetails
        formattedTotalLoanAmount={formattedTotalLoanAmount}
        termYears={termYears}
        formattedPropertyValue={formattedPropertyValue}
        productLTV={productLTV}
      />
    </HeaderContainer>
  )
}

const TextContainer = styled.div({
  margin: "1rem 0",
})

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  & > p {
    margin: 0;
    font-weight: 500;
    font-size: 14px;
    color: rgba(59, 66, 88, 1);
    line-height: 24px;
  }
`

const Header = styled.h1`
  font-size: 39px;
  font-weight: 550;
  min-width: 60%;
`

const TitleContainer = styled.div({
  display: "flex",
  justifyContent: "space-between",

  [mobile]: {
    flexDirection: "column",
    justifyContent: "start",
  },
})

const Subheader = styled.h1`
  font-size: 18px;
  font-weight: 600;
`

const SummaryCheckDateDesktop = styled.div({
  display: "flex",
  alignSelf: "top",
  ["& > span"]: {
    marginLeft: "1rem",
  },
  [tablet]: {
    display: "none",
  },
  [mobile]: {
    display: "none",
  },
})
